import React from 'react'
import { convertBytesToKB } from 'utils/general'
import './FileUpload.css'

/**
 * Componente para mostrar previsualizaciones de archivos
 * 
 * @param {Object} props
 * @param {Array} props.files - Array de archivos a mostrar
 * @param {Function} props.onRemove - Función a llamar cuando se elimina un archivo
 * @param {Function} props.onUndo - Función a llamar cuando se deshace la eliminación de un archivo
 * @param {String} props.title - Título para la sección de archivos
 * @param {Boolean} props.showFileSize - Mostrar o no el tamaño del archivo
 * @param {Boolean} props.showPrincipalBadge - Mostrar o no la etiqueta "Principal" en el primer archivo
 * @param {String} props.emptyMessage - Mensaje a mostrar cuando no hay archivos
 * @param {Array} props.markedForDeletion - IDs de archivos marcados para eliminación
 * @param {Boolean} props.useImageCover - Usar object-fit: cover en lugar de contain para las imágenes
 */
const FilePreview = ({
	files = [],
	onRemove = null,
	onUndo = null,
	title = "Archivos seleccionados",
	showFileSize = true,
	showPrincipalBadge = false,
	emptyMessage = "No hay archivos seleccionados",
	markedForDeletion = [],
	useImageCover = false
}) => {
	// Filtrar archivos visibles y marcados para eliminación
	const visibleFiles = files.filter(file =>
		!file.id || !markedForDeletion.includes(file.id)
	)

	const deletedFiles = files.filter(file =>
		file.id && markedForDeletion.includes(file.id)
	)

	// Si no hay archivos (ni visibles ni eliminados), mostrar mensaje vacío
	if (visibleFiles.length === 0 && deletedFiles.length === 0) {
		return (
			<div className="file-preview-section">
				<h6 className="mb-3">{title}</h6>
				<p className="text-muted">{emptyMessage}</p>
			</div>
		)
	}

	// Determinar si un archivo es una imagen
	const isImageFile = (file) => {
		// Verificar por tipo MIME si está disponible
		if (file.type && file.type.startsWith('image/')) return true

		// Verificar por extensión en caso de que el tipo no esté disponible
		const fileName = file.nombre || file.name || ''
		const extension = fileName.split('.').pop().toLowerCase()
		return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(extension)
	}

	// Obtener el icono según el tipo de archivo
	const getFileIcon = (file) => {
		// Si es una imagen, no necesitamos un icono
		if (isImageFile(file)) return null

		// Obtener la extensión del archivo
		const fileName = file.nombre || file.name || ''
		const extension = fileName.split('.').pop().toLowerCase()

		// Retornar el icono según la extensión
		switch (extension) {
			case 'pdf':
				return 'fa-file-pdf'
			case 'doc':
			case 'docx':
				return 'fa-file-word'
			case 'xls':
			case 'xlsx':
				return 'fa-file-excel'
			case 'ppt':
			case 'pptx':
				return 'fa-file-powerpoint'
			case 'txt':
				return 'fa-file-alt'
			case 'zip':
			case 'rar':
			case '7z':
				return 'fa-file-archive'
			default:
				return 'fa-file'
		}
	}

	// Obtener el URL de previsualización
	const getPreviewUrl = (file) => {
		// Si es un archivo del sistema de archivos
		if (file instanceof File) {
			return URL.createObjectURL(file)
		}

		// Si ya tiene una URL para previsualización
		if (file.previewUrl) {
			return file.previewUrl
		}

		// Si tiene una URL normal
		if (file.url) {
			return file.url
		}

		// Fallback a null (se mostrará un icono)
		return null
	}

	// Obtener el nombre del archivo
	const getFileName = (file, index) => {
		return file.nombre || file.name || `Archivo ${index + 1}`
	}

	// Obtener el tamaño del archivo
	const getFileSize = (file) => {
		if (file.size) {
			return convertBytesToKB(file.size) + ' kb'
		}
		return null
	}

	return (
		<div className="file-preview-section">
			<h6 className="mb-3">{title}</h6>
			<div className="file-preview-container">
				{/* Renderizar archivos visibles */}
				{visibleFiles.map((file, index) => {
					const isImage = isImageFile(file)
					const fileIcon = !isImage ? getFileIcon(file) : null
					const previewUrl = getPreviewUrl(file)
					const fileName = getFileName(file, index)
					const fileSize = showFileSize ? getFileSize(file) : null

					return (
						<div
							key={file.id || `file-${index}`}
							className={`file-preview ${useImageCover && isImage ? 'image-cover' : ''}`}
							title={fileName}
						>
							{isImage && previewUrl ? (
								<div className="file-preview-image-container">
									<img
										src={previewUrl}
										alt={fileName}
									/>
								</div>
							) : (
								<div className="file-icon">
									<i className={`fas ${fileIcon} fa-3x`}></i>
									<span className="file-name">{fileName}</span>
								</div>
							)}

							{onRemove && (
								<div
									className="remove-btn"
									onClick={() => onRemove(file, index)}
									title="Eliminar archivo"
								>
									<i className="fa fa-times"></i>
								</div>
							)}

							{showPrincipalBadge && index === 0 && (
								<div className="principal-badge">
									Principal
								</div>
							)}

							{fileSize && !isImage && (
								<div className="file-size-badge">
									{fileSize}
								</div>
							)}
						</div>
					)
				})}

				{/* Renderizar archivos marcados para eliminación */}
				{deletedFiles.length > 0 && (
					<>
						{deletedFiles.map((file, index) => {
							const isImage = isImageFile(file)
							const fileIcon = !isImage ? getFileIcon(file) : null
							const previewUrl = getPreviewUrl(file)
							const fileName = getFileName(file, index)

							return (
								<div
									key={file.id || `deleted-file-${index}`}
									className={`file-preview file-preview-marked-for-deletion ${useImageCover && isImage ? 'image-cover' : ''}`}
								>
									{isImage && previewUrl ? (
										<div className="file-preview-image-container">
											<img
												src={previewUrl}
												alt={fileName}
											/>
										</div>
									) : (
										<div className="file-icon">
											<i className={`fas ${fileIcon} fa-3x`}></i>
											<span className="file-name">{fileName}</span>
										</div>
									)}

									{onUndo && (
										<div
											className="undo-btn"
											onClick={() => onUndo(file, index)}
											title="Restaurar archivo"
										>
											<i className="fa fa-undo"></i>
										</div>
									)}

									<div className="deleted-badge">
										Se eliminará
									</div>
								</div>
							)
						})}
					</>
				)}
			</div>
		</div>
	)
}

export default FilePreview